<template>
    <page>
        <div class="d-flex pt-2 pb-1 paygroup-run-header">
            <div class="mr-4">
                <h5 class="text-muted">PAY GROUP</h5>
                <h4>{{ payrollRun.paygroupName }}</h4>
            </div>
            <div class="mr-4">
                <h5 class="text-muted">CHECK DATE</h5>
                <h4>{{ $filters.date(payrollRun.checkDate) }}</h4>
            </div>
            <div class="mr-4">
                <h5 class="text-muted">PAY PERIOD</h5>
                <h4>{{ $filters.date(payrollRun.payPeriodStartDate) }} - {{ $filters.date(payrollRun.payPeriodEndDate) }}</h4>
            </div>
            <payroll-steps class="ml-auto align-self-center" current="summary" :show-salaried="payrollRun.hasSalariedEmployees" />
        </div>
        <info-widget v-if="false" :collapsible="true" :is-expanded="true" class="mt-2">
            <template v-slot:title>Current Payroll Changes</template>
            <slot>
                <div class="p-2"></div>
            </slot>
        </info-widget>
        <info-widget v-if="false" :collapsible="true" :is-expanded="true" class="mt-2">
            <template v-slot:title>Credits</template>
            <slot>
                <div class="p-2"></div>
            </slot>
        </info-widget>
        <info-widget v-if="false" :collapsible="true" :is-expanded="true" class="mt-2">
            <template v-slot:title>Payroll Delivery</template>
            <slot>
                <div class="p-2"></div>
            </slot>
        </info-widget>
        <changes-report
            :changes-report="changesReport"
            :client="client"
            class="mt-2"
        />
        <cash-requirements
            :cash-requirements="cashRequirements"
            :ledger-totals-status="ledgerTotalsStatus"
            :check-date="payrollRun.checkDate"
            class="mt-2"
        />
        <labor-allocation
            :ledger-totals="ledgerTotals"
            :ledger-totals-status="ledgerTotalsStatus"
            class="mt-2"
        />
        <!-- <info-widget :collapsible="true" :is-expanded="true" class="mt-2">
            <template v-slot:title>Additional Reports</template>
            <slot>
                <div class="p-2"></div>
            </slot>
        </info-widget> -->

        <div v-if="ledgerTotalsReady" class="grand-total d-flex justify-content-between pt-2 pb-2 pl-2 pr-2 mt-3">
            <h3 class="mb-0">Amount to Withdraw</h3>
            <h3 class="mb-0">${{ electronicWithdrawalsTotal }}</h3>
        </div>

        <div v-if="ledgerTotalsReady" class="mt-3">
            <h3>Acceptance</h3>
            <p>
                By clicking "Approve and Authorize Funding," you agree that all employee pay information is correct. You agree that the total sum of <strong>${{ electronicWithdrawalsTotal }}</strong> will be withdrawn electronically from your bank accounts via EFT on <strong>{{ $filters.datelong(payrollRun.checkDate) }}</strong>. This action cannot be canceled once it has begun.
            </p>
        </div>

        <button-row inline>
            <button type="button" class="btn btn-outline-primary" @click.prevent="goToPaygrid">Back</button>
            <div>
            <button type="button" class="btn btn-outline-primary" :disabled="isRunComplete" @click.prevent="showSubmitLocationsModal = true">Submit for Approval</button>
            <!-- Approval is not yet gated on the funding report; the report backend adds the gate. -->
                <button type="button" class="btn btn-primary" :disabled="isRunComplete" @click.prevent="showApproveLocationsModal = true">Approve and Authorize Funding</button>
            </div>
        </button-row>

        <submit-locations-modal
            v-if="showSubmitLocationsModal"
            :client="client"
            :payroll-run="payrollRun"
            @close="showSubmitLocationsModal = false"
            @submitted="onSubmitted"
        />

        <approve-locations-modal
            v-if="showApproveLocationsModal"
            :client="client"
            :payroll-run="payrollRun"
            @close="showApproveLocationsModal = false"
            @approved="onApproved"
            @approving-payroll="onApprovingPayroll"
        />
    </page>
</template>

<script>
import Page from '@/components/Page'
import ButtonRow from '@/components/ButtonRow'
import InfoWidget from '@/components/InfoWidget'
import SubmitLocationsModal from './SubmitLocationsModal'
import ApproveLocationsModal from './ApproveLocationsModal'
import LaborAllocation from './LaborAllocation'
import CashRequirements from './CashRequirements'
import ChangesReport from './ChangesReport'
import PayrollSteps from './components/PayrollSteps'

export default {
    components: {Page, ButtonRow, InfoWidget, SubmitLocationsModal, ApproveLocationsModal, LaborAllocation, CashRequirements, ChangesReport, PayrollSteps},
    props: ['client'],
    data() {
        return {
            dataLoaded: false,
            ledgerTotalsStatus: null,
            showSubmitLocationsModal: false,
            showApproveLocationsModal: false,
            payrollRun: {
                id: this.$route.params.runId,
                paygroupName: '',
                // alerts: [],
                checkDate: null,
                payPeriodStartDate: null,
                payPeriodEndDate: null,
                lastSavedTime: '',
                companies: [],
                workLocations: [],
                submittedLocationIds: [],
                approvedLocationIds: [],
                hasSalariedEmployees: true,
                status: null,
            },
            ledgerTotals: null,
            changesReport: null,
            completingTaskId: null,
        }
    },
    computed: {
        // Approved runs complete immediately, so both statuses mean the run is done
        isRunComplete() {
            return ['approved', 'completed'].includes(this.payrollRun.status)
        },
        // The Labor Allocation report has successfully finished computing. Gates the Acceptance
        // text and the Approve button so neither is shown/enabled until the funding total is
        // ready. A 'failed' report intentionally keeps approval blocked, since it likely signals
        // a problem that would also surface when processing the payroll for real.
        ledgerTotalsReady() {
            return this.ledgerTotalsStatus === 'ready'
        },
        // Cash Requirements report (computed/cached alongside the Labor Allocation report).
        cashRequirements() {
            return this.ledgerTotals ? this.ledgerTotals.cash_requirements : null
        },
        // "Amount to Withdraw": the total of the Electronic Withdrawals section, which already
        // covers direct-deposit net pay plus all payroll taxes (employee + employer) funded by EFT.
        electronicWithdrawalsTotal() {
            const accounts = (this.cashRequirements && this.cashRequirements.electronic_withdrawals) || []
            const sum = accounts.reduce((acc, account) => acc + (parseFloat(account.amount) || 0), 0)
            return sum.toFixed(2)
        },
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Payroll')
        // Load the run, then ensure the Labor Allocation report is current (recomputed only
        // if the run changed since last time). Serializing these avoids a race where the read
        // could clobber a just-triggered "computing" state.
        this.getData().then(() => {
            this.ensureLedgerTotals()
        })
    },
    unmounted() {
        this._stopPolling()
        this._stopCompletionPolling()
    },
    methods: {
        getData(silent) {
            if (!silent) {
                this.dataLoaded = false
                this.$store.dispatch('START_LOADING')
            }
            this.refreshing = true

            let url
            const paygroupId = this.$route.params.paygroupId
            const collectionId = this.$route.params.collectionId
            const payPeriodStart = this.$route.query.pay_period_start_date
            if (collectionId) {
                url = `/clients/${this.client.id}/payroll/collections/${collectionId}/summary?pay_period_start_date=${payPeriodStart}`
            } else if (paygroupId && this.payrollRun.id) {
                url = `/clients/${this.client.id}/payroll/runs/${this.payrollRun.id}/summary`
            }

            // let resp = paygridData

            return this.$api.get(url).then(resp => {
                this.payrollRun.companies = resp['companies']
                this.payrollRun.workLocations = resp['work_locations'] || []
                this.payrollRun.submittedLocationIds = resp['submitted_location_ids'] || []
                this.payrollRun.approvedLocationIds = resp['approved_location_ids'] || []
                this.payrollRun.paygroupName = resp['paygroup_name']
                this.payrollRun.payPeriodStartDate = resp['pay_period_start_date']
                this.payrollRun.payPeriodEndDate = resp['pay_period_end_date']
                this.payrollRun.checkDate = resp['check_date']
                this.payrollRun.lastSavedTime = resp['last_saved_time']
                this.payrollRun.hasSalariedEmployees = Boolean(resp['has_salaried_employees'])
                this.payrollRun.status = resp['status']
                this.ledgerTotals = resp['ledger_totals']
                this.ledgerTotalsStatus = resp['ledger_totals_status']
                this.changesReport = resp['changes_report'] || null

                if (this.ledgerTotalsStatus === 'computing') {
                    this._startPolling()
                } else {
                    this._stopPolling()
                }

                this.dataLoaded = true
                this.refreshing = false
                if (!silent) {
                    this.$store.dispatch('STOP_LOADING')
                }
            }).catch((errors) => {
                if (errors.__status__ == 403) {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.$emit('no-access')
                    return
                }
                if (!silent) {
                    this.$store.dispatch('STOP_LOADING')
                }
                this.refreshing = false
                this.$bus.showError(errors.__all__)
            })
        },
        ensureLedgerTotals() {
            // Ensure the Labor Allocation report is up to date. The endpoint recomputes only
            // if the run changed since it was last computed; otherwise the cached report that
            // getData() already loaded is still valid and we render it instantly. Skipped for
            // collection summaries (the recompute endpoint is run-scoped). Non-fatal on error.
            if (this.$route.params.collectionId || !this.payrollRun.id) {
                return
            }
            const url = `/clients/${this.client.id}/payroll/runs/${this.payrollRun.id}/ledger-totals/recompute`
            this.$api.post(url, {}).then(resp => {
                this.ledgerTotalsStatus = resp['ledger_totals_status']
                if (this.ledgerTotalsStatus === 'computing') {
                    // A fresh compute was kicked off; drop the now-stale cache and poll for it.
                    this.ledgerTotals = null
                    this._startPolling()
                }
                // 'ready': the cached report loaded by getData() is current -- render as-is.
            }).catch(() => {
                // Leave the rest of the summary working if the report fails to start.
            })
        },
        _startPolling() {
            if (this._ledgerTotalsPoller) return
            this._ledgerTotalsPoller = setInterval(() => { this.getData(true) }, 3000)
        },
        _stopPolling() {
            if (this._ledgerTotalsPoller) {
                clearInterval(this._ledgerTotalsPoller)
                this._ledgerTotalsPoller = null
            }
        },
        goToPaygrid() {
            // TODO KB PG logic for collection summary screen
            // Back goes to the last paygrid step: salaried, unless the run skipped it
            const step = this.payrollRun.hasSalariedEmployees ? '/salaried' : ''
            this.$router.push({
                path: `/clients/${this.$route.params.hashedClientId}/payroll/paygrid/paygroup/${this.$route.params.paygroupId}/run/${this.$route.params.runId}${step}`
            })
        },
        goToStartScreen() {
            this.$router.push({
                path: `/clients/${this.$route.params.hashedClientId}/payroll/`,
            })
        },
        onSubmitted() {
            this.goToStartScreen()
        },
        onApproved() {
            this.getData(true)
        },
        onApprovingPayroll(taskId) {
            this.completingTaskId = taskId
            this.$store.dispatch('START_LOADING')
            this._startCompletionPolling()
        },
        _startCompletionPolling() {
            if (this._completionPoller) return
            this._completionPoller = setInterval(() => {
                const url = `/clients/${this.client.id}/payroll/runs/${this.payrollRun.id}/complete/progress?task_id=${this.completingTaskId}`
                this.$api.get(url).then(resp => {
                    if (resp.status === 'SUCCESS') {
                        this._stopCompletionPolling()
                        this.$store.dispatch('STOP_LOADING')
                        this.$bus.showInfo('Successfully completed payroll.')
                        this.goToStartScreen()
                    } else if (resp.status === 'FAILURE') {
                        this._stopCompletionPolling()
                        this.$store.dispatch('STOP_LOADING')
                        this.$bus.showError(resp.error || 'Payroll completion failed.')
                    }
                }).catch((errors) => {
                    this._stopCompletionPolling()
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }, 2000)
        },
        _stopCompletionPolling() {
            if (this._completionPoller) {
                clearInterval(this._completionPoller)
                this._completionPoller = null
            }
        },
    },
}
</script>

<style lang="scss" scoped>
    .paygroup-run-header {
        h5 {
            font-weight: 500;
        }
        border-bottom: 1px solid #ddd;
    }
    .table-indent-1 {
        padding-left: 2rem !important;
    }
    .vertical-align-middle {
        vertical-align: middle;
    }
    .tr-height {
        height: 32px;
    }
    .grand-total {
        background-color: #F7F7F7;
        border: 2px solid #ccc;
    }
</style>
